import React, { useEffect, useState } from "react";
import { Plus, Check, X, Edit3, Users, Save } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin / Ops" },
  { value: "sales_manager", label: "Sales Manager" },
  { value: "sales_exec", label: "Sales Executive" },
  { value: "bd", label: "BD User" },
  { value: "viewer", label: "Viewer" },
];

function emptyForm() {
  return { email: "", name: "", password: "", role: "sales_exec", phone: "" };
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editPhone, setEditPhone] = useState("");
  const [defaultOwnerId, setDefaultOwnerId] = useState("");
  const [defaultOwnerSaving, setDefaultOwnerSaving] = useState(false);

  const load = async () => {
    try {
      const [u, s] = await Promise.all([
        api.get("/users"),
        api.get("/admin/settings").catch(() => ({ data: {} })),
      ]);
      setUsers(u.data);
      setDefaultOwnerId(s.data?.default_lead_owner_id || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load users");
    }
  };

  const saveDefaultOwner = async () => {
    setDefaultOwnerSaving(true);
    try {
      await api.put("/admin/settings/default_lead_owner_id", { value: defaultOwnerId || null });
      toast.success(defaultOwnerId ? "Default lead owner updated" : "Default lead owner cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setDefaultOwnerSaving(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...form, phone: form.phone || null });
      toast.success("User created");
      setForm(emptyForm());
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    if (u.id === currentUser?.id) {
      toast.error("You can't change your own role");
      return;
    }
    try {
      await api.patch(`/users/${u.id}`, { role: newRole });
      toast.success(`Role updated to ${ROLES.find((r) => r.value === newRole)?.label || newRole}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const toggle = async (u) => {
    if (u.id === currentUser?.id) {
      toast.error("You can't deactivate your own account");
      return;
    }
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active !== false ? "User deactivated" : "User reactivated");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const toggleAutoAssign = async (u) => {
    const next = !u.auto_assign_eligible;
    try {
      await api.patch(`/users/${u.id}`, { auto_assign_eligible: next });
      toast.success(next ? `${u.name} added to auto-assign pool` : `${u.name} removed from pool`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  const startEditPhone = (u) => {
    setEditingId(u.id);
    setEditPhone(u.phone || "");
  };

  const savePhone = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { phone: editPhone || null });
      setEditingId(null);
      load();
      toast.success("Phone updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <Layout title="Users & Roles" breadcrumb={["Admin", "Users"]}>
      {isSuperAdmin && (
        <div className="mb-5 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-5 shadow-sm" data-testid="default-lead-owner-card">
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-orange-500 text-white rounded-lg p-2"><Users size={16} /></div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-900">Default lead owner (lead pool)</h3>
              <p className="text-xs text-slate-600 mt-0.5">
                New leads created via the UI, public form, API, or bulk import are auto-assigned to this user.
                Any other sales rep can then "pick" them from the workbench. Leave blank to assign new leads to whoever creates them.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              data-testid="default-lead-owner-select"
              value={defaultOwnerId}
              onChange={(e) => setDefaultOwnerId(e.target.value)}
              className="text-sm px-3 py-2 border border-orange-200 rounded-md bg-white flex-1 min-w-[260px]"
            >
              <option value="">— No pool owner (creator becomes owner) —</option>
              {users.filter((u) => u.active !== false).map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))}
            </select>
            <button
              data-testid="default-lead-owner-save"
              onClick={saveDefaultOwner}
              disabled={defaultOwnerSaving}
              className="text-sm font-medium px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white inline-flex items-center gap-1.5"
            >
              <Save size={13} /> {defaultOwnerSaving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <form onSubmit={create} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3 h-fit">
          <h3 className="text-base font-semibold text-slate-900 mb-1">Invite a teammate</h3>
          <input data-testid="user-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="w-full px-3 py-2 text-sm rounded-md border border-slate-200" />
          <input data-testid="user-form-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-3 py-2 text-sm rounded-md border border-slate-200" />
          <input data-testid="user-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (used in email signatures)" className="w-full px-3 py-2 text-sm rounded-md border border-slate-200" />
          <input data-testid="user-form-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password" className="w-full px-3 py-2 text-sm rounded-md border border-slate-200" />
          <select data-testid="user-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 text-sm rounded-md border border-slate-200">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            The phone number is available as <code className="px-1 rounded bg-slate-100 text-slate-700 text-[10px]">{`{{rep_phone}}`}</code> in email templates and is auto-included in outgoing email signatures when used.
          </p>
          <button data-testid="user-form-submit" className="w-full px-3 py-2 text-sm font-medium rounded-md bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center justify-center gap-1.5">
            <Plus size={14} /> Create user
          </button>
        </form>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">All users</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>{["Name", "Email", "Phone", "Role", "Status", "Auto-assign", ""].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}</tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-800">{u.name}</td>
                  <td className="px-4 py-3 text-slate-600">{u.email}</td>
                  <td className="px-4 py-3 text-slate-700" data-testid={`user-phone-${u.id}`}>
                    {editingId === u.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          data-testid={`user-phone-input-${u.id}`}
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          placeholder="+91 9999..."
                          className="px-2 py-1 text-xs rounded border border-slate-200 w-32"
                          autoFocus
                        />
                        <button onClick={() => savePhone(u)} data-testid={`user-phone-save-${u.id}`} className="p-1 rounded hover:bg-emerald-50 text-emerald-600" title="Save">
                          <Check size={14} />
                        </button>
                        <button onClick={() => setEditingId(null)} className="p-1 rounded hover:bg-slate-100 text-slate-500" title="Cancel">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => startEditPhone(u)} data-testid={`user-phone-edit-${u.id}`} className="inline-flex items-center gap-1.5 text-left hover:text-orange-600 group">
                        <span className={u.phone ? "" : "text-slate-400 italic"}>{u.phone || "Not set"}</span>
                        <Edit3 size={11} className="text-slate-300 group-hover:text-orange-500" />
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.id === currentUser?.id ? (
                      <span className="text-xs font-medium bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full border border-orange-200" data-testid={`user-role-readonly-${u.id}`}>
                        {ROLES.find((r) => r.value === u.role)?.label || u.role}
                      </span>
                    ) : (
                      <select
                        data-testid={`user-role-select-${u.id}`}
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className="text-xs font-medium px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-800 cursor-pointer hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200"
                      >
                        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {u.id === currentUser?.id ? (
                      <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700" data-testid={`user-status-readonly-${u.id}`}>You</span>
                    ) : (
                      <button
                        data-testid={`user-toggle-${u.id}`}
                        onClick={() => toggle(u)}
                        title={u.active !== false ? "Click to deactivate" : "Click to reactivate"}
                        className={`text-xs px-2.5 py-0.5 rounded-full font-medium border transition-colors ${u.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"}`}
                      >
                        {u.active !== false ? "Active" : "Inactive"}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`user-auto-assign-toggle-${u.id}`}
                      onClick={() => toggleAutoAssign(u)}
                      title={u.auto_assign_eligible ? "In auto-assign pool — click to remove" : "Click to add to auto-assign pool"}
                      className={`text-xs px-2.5 py-0.5 rounded-full font-medium border transition-colors ${u.auto_assign_eligible ? "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100" : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"}`}
                    >
                      {u.auto_assign_eligible ? "In pool" : "Off"}
                    </button>
                  </td>
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
